import React from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsTooltip } from '@/core/constants/webComponents';

interface PostStatsProps {
  platform: string;
  likes: number | null;
  shares: number | null;
  comments: number | null;
  plays: number | null;
  followers: number | null;
}

const formatStat = (num: number | null): string | null => {
  if (num === null) return null;

  const lookup = [
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' }
  ];

  const item = lookup.find(item => num >= item.value);

  if (!item) return num.toString();

  const divided = num / item.value;
  const decimalPlaces = divided >= 100 ? 0 : 1;
  const formatted = Number(divided.toFixed(decimalPlaces));
  
  return `${formatted}${item.symbol}`;
};

export const PostStats: React.FC<PostStatsProps> = ({ platform, likes, shares, comments, plays, followers }) => {
  const { t, i18n } = useTranslation();
  const noData = t('cardPosts.noData');
  const isYoutube = platform === 'youtube';
  const statClassName = `post-card__stat ${isYoutube ? 'post-card__stat--youtube-item' : ''}`;

  const getTooltipText = (num: number | null, labelKey: string, noDataKey: string) => {
    if (num === null) {
      return t('cardPosts.' + noDataKey);
    }
    const translatedLabel = t('cardPosts.' + labelKey);
    return `${num.toLocaleString(i18n.language)} ${translatedLabel}`;
  };

  return (
    <div className={`post-card__stats ${isYoutube ? 'post-card__stats--youtube' : ''}`}>
      <BdsTooltip position="top-right" tooltip-text={getTooltipText(likes, 'likes', 'noLikes')}>
        <span className={statClassName}>
          <BdsIcon theme="outline" name="heart" size="small" />
          <span>{formatStat(likes) ?? noData}</span>
        </span>
      </BdsTooltip>
      <BdsTooltip position="top-center" tooltip-text={getTooltipText(shares, 'shares', 'noShares')}>
        <span className={statClassName}>
          <BdsIcon theme="outline" name="share" size="small" />
          <span>{formatStat(shares) ?? noData}</span>
        </span>
      </BdsTooltip>
      <BdsTooltip position="top-center" tooltip-text={getTooltipText(comments, 'comments', 'noComments')}>
        <span className={statClassName}>
          <BdsIcon theme="outline" name="message-ballon" size="small" />
          <span>{formatStat(comments) ?? noData}</span>
        </span>
      </BdsTooltip>
      <BdsTooltip position="top-center" tooltip-text={getTooltipText(plays, 'plays', 'noPlays')}>
        <span className={statClassName}>
          <BdsIcon theme="outline" name="play" size="small" />
          <span>{formatStat(plays) ?? noData}</span>
        </span>
      </BdsTooltip>
      <BdsTooltip position="top-right" tooltip-text={getTooltipText(followers, 'followers', 'noFollowers')}>
        <span className={statClassName}>
          <BdsIcon theme="outline" name="team" size="small" />
          <span>{formatStat(followers) ?? noData}</span>
        </span>
      </BdsTooltip>
      
      {isYoutube && (
        <BdsTooltip position="top-right" tooltip-text={t('cardPosts.youtubeTooltipInfo')}>
          <span className="post-card__stat post-card__stat--info">
            <BdsIcon theme="outline" name="info" size="small" />
          </span>
        </BdsTooltip>
      )}
    </div>
  );
};